// Calendar-only arithmetic prevents timezone and daylight-saving shifts.
export function calendarDate(value: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(value + 'T00:00:00.000Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
}
export function todayDate(): string { return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lagos', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()); }
export function ageFromDob(dob: string, today = todayDate()): number | null {
    const birth = calendarDate(dob), now = calendarDate(today);
    if (!birth || !now || birth > now) return null;
    let age = now.getUTCFullYear() - birth.getUTCFullYear();
    if (now.getUTCMonth() < birth.getUTCMonth() || (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() < birth.getUTCDate())) age--;
    return age >= 0 && age <= 130 ? age : null;
}
export function eddFromLmp(lmp: string): string | null {
    const date = calendarDate(lmp); if (!date) return null;
    date.setUTCDate(date.getUTCDate() + 280); return date.toISOString().slice(0, 10);
}
export function daysToDueDate(edd: string, today = todayDate()): number | null {
    const due = calendarDate(edd), now = calendarDate(today);
    return due && now ? Math.round((due.getTime() - now.getTime()) / 86400000) : null;
}
export interface AncDeliveryHistory {
    year: number | null;
    mode: 'vaginal' | 'assisted_vaginal' | 'caesarean' | 'other' | 'unknown';
    outcome: string;
    complications: string;
}
export interface AncRegistrationDetails {
    lmp: string | null;
    lmp_certain: boolean | null;
    edd: string | null;
    edd_source: 'lmp' | 'ultrasound' | 'assisted_reproduction' | 'clinical_estimate' | 'unknown';
    dating_notes: string;
    menstrual_cycle_days: number | null;
    menstrual_duration_days: number | null;
    menstrual_flow: 'heavy' | 'moderate' | 'normal' | 'irregular' | 'absent' | 'unknown';
    gravidity: number | null;
    parity: number | null;
    living_children: number | null;
    pregnancy_losses: number | null;
    fetuses: number | null;
    booking_date: string | null;
    previous_deliveries: AncDeliveryHistory[];
    risk_notes: string;
    care_plan: string;
}
export function validateAncDetails(value: unknown, today = todayDate()): string | null {
    if (value === null) return null;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return 'invalid_anc_details';
    const v = value as Record<string, unknown>;
    const expected = ['lmp','lmp_certain','edd','edd_source','dating_notes','menstrual_cycle_days','menstrual_duration_days','menstrual_flow','gravidity','parity','living_children','pregnancy_losses','fetuses','booking_date','previous_deliveries','risk_notes','care_plan'];
    if (Object.keys(v).some(k => !expected.includes(k))) return 'invalid_anc_field';
    for (const k of ['lmp','edd','booking_date']) if (v[k] !== null && (typeof v[k] !== 'string' || !calendarDate(v[k] as string))) return 'invalid_anc_' + k;
    if (typeof v.lmp === 'string' && v.lmp > today) return 'future_lmp';
    if (typeof v.lmp === 'string' && typeof v.edd === 'string' && v.edd <= v.lmp) return 'edd_before_lmp';
    if (v.lmp_certain !== null && typeof v.lmp_certain !== 'boolean') return 'invalid_lmp_certainty';
    if (!['lmp','ultrasound','assisted_reproduction','clinical_estimate','unknown'].includes(String(v.edd_source))) return 'invalid_edd_source';
    if (v.edd_source === 'lmp' && (!v.lmp || v.edd !== eddFromLmp(v.lmp as string))) return 'invalid_lmp_edd';
    if (!['heavy','moderate','normal','irregular','absent','unknown'].includes(String(v.menstrual_flow))) return 'invalid_menstrual_flow';
    for (const k of ['menstrual_cycle_days','menstrual_duration_days','gravidity','parity','living_children','pregnancy_losses','fetuses']) {
        const n = v[k]; if (n !== null && (typeof n !== 'number' || !Number.isInteger(n) || n < 0 || n > (k === 'menstrual_cycle_days' ? 120 : 50))) return 'invalid_anc_' + k;
    }
    if (typeof v.parity === 'number' && typeof v.gravidity === 'number' && v.parity > v.gravidity) return 'parity_exceeds_gravidity';
    for (const k of ['dating_notes','risk_notes','care_plan']) if (typeof v[k] !== 'string' || (v[k] as string).length > 4000) return 'invalid_anc_' + k;
    if (!Array.isArray(v.previous_deliveries) || v.previous_deliveries.length > 30) return 'invalid_previous_deliveries';
    for (const row of v.previous_deliveries) {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return 'invalid_previous_delivery';
        const d = row as Record<string, unknown>;
        if (Object.keys(d).some(k => !['year','mode','outcome','complications'].includes(k))) return 'invalid_previous_delivery';
        if (d.year !== null && (typeof d.year !== 'number' || !Number.isInteger(d.year) || d.year < 1900 || d.year > Number(today.slice(0,4)))) return 'invalid_delivery_year';
        if (!['vaginal','assisted_vaginal','caesarean','other','unknown'].includes(String(d.mode))) return 'invalid_delivery_mode';
        for (const k of ['outcome','complications']) if (typeof d[k] !== 'string' || (d[k] as string).length > 1000) return 'invalid_delivery_notes';
    }
    return null;
}
